import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Progress } from "~/components/ui/progress";
import { Textarea } from "~/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type {
  Plant,
  PurchaseOrderItem,
  ReceptionItemFormData,
} from "~/types/purchasing";
import {
  CURRENCY_SYMBOLS,
  PLANT_OPTIONS,
  UNIT_LABELS,
} from "~/types/purchasing";

interface ReceptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: PurchaseOrderItem[];
  currency: string;
  purchaseOrderId: string;
  onSubmit: (
    items: ReceptionItemFormData[],
    plant: Plant,
    observations?: string
  ) => void;
  isLoading: boolean;
}

interface ItemReceptionState {
  purchase_order_item_id: string;
  quantity_received: number;
  actual_unit_cost: number;
  expiry_date: string;
  remaining: number;
  original_unit_price: number;
}

function calculateInitialState(
  items: PurchaseOrderItem[]
): ItemReceptionState[] {
  return items.map((item) => {
    const remaining = Math.max(
      0,
      item.quantity - (item.received_quantity || 0)
    );
    return {
      purchase_order_item_id: item.id,
      quantity_received: remaining,
      actual_unit_cost: item.unit_price,
      expiry_date: "",
      remaining,
      original_unit_price: item.unit_price,
    };
  });
}

export function ReceptionDialog({
  open,
  onOpenChange,
  items,
  currency,
  purchaseOrderId,
  onSubmit,
  isLoading,
}: ReceptionDialogProps) {
  const [observations, setObservations] = useState("");
  const [receptionDate, setReceptionDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [plant, setPlant] = useState<Plant>("P1");
  const [itemStates, setItemStates] = useState<ItemReceptionState[]>(() =>
    calculateInitialState(items)
  );

  const currencySymbol =
    CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || "$";

  // Sync state when dialog opens or items change
  useEffect(() => {
    if (open) {
      setItemStates(calculateInitialState(items));
      setObservations("");
      setReceptionDate(dayjs().format("YYYY-MM-DD"));
      setPlant("P1");
    }
  }, [open, items]);

  const updateItemState = (
    itemId: string,
    field: keyof ItemReceptionState,
    value: number | string
  ) => {
    setItemStates((prev) =>
      prev.map((state) => {
        if (state.purchase_order_item_id !== itemId) return state;

        if (field === "quantity_received") {
          // Only prevent negative values - allow receiving more than ordered
          const clampedValue = Math.max(0, value as number);
          return { ...state, quantity_received: clampedValue };
        }

        return { ...state, [field]: value };
      })
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const itemsToReceive: ReceptionItemFormData[] = itemStates
      .filter((state) => state.quantity_received > 0)
      .map((state) => ({
        purchase_order_item_id: state.purchase_order_item_id,
        quantity_received: state.quantity_received,
        actual_unit_cost: state.actual_unit_cost,
        expiry_date: state.expiry_date || undefined,
      }));
    onSubmit(itemsToReceive, plant, observations || undefined);
  };

  const totalToReceive = itemStates.reduce(
    (sum, s) => sum + s.quantity_received,
    0
  );
  const hasItemsToReceive = totalToReceive > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Recepción de Items</DialogTitle>
          <DialogDescription>
            Ingrese la cantidad recibida, costo real y fecha de vencimiento para
            cada item.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Reception Date and Plant */}
          <div className="flex flex-wrap items-center gap-4 border-b pb-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="reception-date" className="whitespace-nowrap">
                Fecha:
              </Label>
              <Input
                id="reception-date"
                type="date"
                value={receptionDate}
                onChange={(e) => setReceptionDate(e.target.value)}
                className="h-9 w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="plant" className="whitespace-nowrap">
                Planta:
              </Label>
              <Select value={plant} onValueChange={(v) => setPlant(v as Plant)}>
                <SelectTrigger id="plant" className="h-9 w-40">
                  <SelectValue placeholder="Seleccionar planta" />
                </SelectTrigger>
                <SelectContent>
                  {PLANT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            {items.map((item) => {
              const state = itemStates.find(
                (s) => s.purchase_order_item_id === item.id
              );
              const received = item.received_quantity || 0;
              const remaining = state?.remaining || 0;
              const progress = (received / item.quantity) * 100;
              const unitLabel = UNIT_LABELS[item.unit] || item.unit;
              const costChanged =
                state && state.actual_unit_cost !== state.original_unit_price;

              return (
                <div
                  key={item.id}
                  className="space-y-3 rounded-lg border bg-muted/30 p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {item.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {unitLabel} · Precio OC: {currencySymbol}
                        {item.unit_price.toLocaleString("es-CL")}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-medium">
                        {received} / {item.quantity}
                      </p>
                      <p className="text-xs text-muted-foreground">recibido</p>
                    </div>
                  </div>

                  <Progress value={progress} className="h-2" />

                  {remaining <= 0 && (
                    <p className="text-xs font-medium text-amber-600">
                      Ya recibido: {received} de {item.quantity} ordenados
                    </p>
                  )}

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {/* Quantity */}
                    <div className="space-y-1">
                      <Label htmlFor={`qty-${item.id}`} className="text-xs">
                        Cantidad a recibir
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id={`qty-${item.id}`}
                          type="number"
                          min={0}
                          step="any"
                          value={state?.quantity_received || 0}
                          onChange={(e) =>
                            updateItemState(
                              item.id,
                              "quantity_received",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-8"
                        />
                        {remaining > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              updateItemState(
                                item.id,
                                "quantity_received",
                                remaining
                              )
                            }
                            className="h-8 px-2 text-xs"
                          >
                            Todo
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {remaining > 0
                          ? `${remaining} pendientes de ${item.quantity} ordenados`
                          : `Ordenado: ${item.quantity}`}
                      </p>
                    </div>

                    {/* Actual Cost */}
                    <div className="space-y-1">
                      <Label htmlFor={`cost-${item.id}`} className="text-xs">
                        Costo real unitario
                      </Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {currencySymbol}
                        </span>
                        <Input
                          id={`cost-${item.id}`}
                          type="number"
                          min={0}
                          step="0.01"
                          value={state?.actual_unit_cost || 0}
                          onChange={(e) =>
                            updateItemState(
                              item.id,
                              "actual_unit_cost",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-8 pl-6"
                        />
                      </div>
                      {costChanged && (
                        <p className="text-xs text-amber-600">
                          Diferente al precio OC
                        </p>
                      )}
                    </div>

                    {/* Expiry Date */}
                    <div className="space-y-1">
                      <Label
                        htmlFor={`expiry-${item.id}`}
                        className="text-xs"
                      >
                        Fecha vencimiento
                      </Label>
                      <Input
                        id={`expiry-${item.id}`}
                        type="date"
                        value={state?.expiry_date || ""}
                        onChange={(e) =>
                          updateItemState(
                            item.id,
                            "expiry_date",
                            e.target.value
                          )
                        }
                        className="h-8"
                      />
                      <p className="text-xs text-muted-foreground">
                        Opcional
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-2">
            <Label htmlFor="observations">Observaciones (opcional)</Label>
            <Textarea
              id="observations"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Notas sobre la recepción..."
              rows={2}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || !hasItemsToReceive}>
              {isLoading ? "Procesando..." : "Confirmar Recepción"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
